import type { ChainData } from "@0xsquid/squid-types";
import { ChainType } from "@0xsquid/squid-types";
import { OfflineSigner } from "@cosmjs/proto-signing";
import type { ChainInfo, Keplr } from "@keplr-wallet/types";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CHAIN_IDS } from "../../core/constants";
import { ICosmosContextData } from "../../core/providers/CosmosProvider";
import { CosmosWallet, Wallet } from "../../core/types/wallet";
import {
  getCosmosChainInfosObject,
  getCosmosKey,
  suggestChainOrThrow,
} from "../../services/internal/cosmosService";
import {
  isUserRejectionError,
  normalizeError,
} from "../../services/internal/errorService";
import { getConnectorForChainType } from "../../services/internal/walletService";
import { useSquidChains } from "../chains/useSquidChains";
import { useClient } from "../client/useClient";
import { useSwapRoutePersistStore } from "../store/useSquidStore";
import { useWalletStore } from "../store/useWalletStore";
import { useSwap } from "../swap/useSwap";

export const useCosmos = (): ICosmosContextData => {
  const cosmosWalletState = useWalletStore(
    (state) => state.connectedWalletsByChainType[ChainType.COSMOS]
  );

  const cosmosAddress = cosmosWalletState?.address;
  const isConnected = !!cosmosAddress;
  const cosmosConnectedWallet = cosmosWalletState.wallet;

  const [cosmosChainId, setCosmosChainId] = useState<string | undefined>();
  const { fromChain } = useSwap();
  const { cosmosChains } = useSquidChains();
  const { clientWindow } = useClient();
  const { setConnectedWallet, disconnectWallet } = useWalletStore();
  const { keplrTypeWallet, signer: cosmosSigner } = useCosmosSigner({
    chain: fromChain,
  });

  const getCosmosAddressForChain = useCallback(
    async (chainId?: string) => {
      return getCosmosKey(chainId, keplrTypeWallet);
    },
    [keplrTypeWallet]
  );

  const getAddress = useCallback(
    async ({
      cosmosWalletObject,
      chainId,
    }: {
      wallet: Wallet;
      cosmosWalletObject: any;
      chainId: string;
    }): Promise<string> => {
      const address = await getCosmosKey(chainId, cosmosWalletObject);
      return address ?? "";
    },
    []
  );

  /**
   * Update wallet store with the new connected wallet
   * and update swap route store with the new derived fallback address
   */
  const updateWalletStore = useCallback(
    async (
      wallet: Wallet | undefined,
      cosmosWalletObject: any,
      address: string
    ) => {
      const osmosisAddress = await getCosmosKey(
        CHAIN_IDS.OSMOSIS,
        cosmosWalletObject
      );

      if (wallet) {
        setConnectedWallet(ChainType.COSMOS, {
          wallet: wallet as CosmosWallet,
          address,
        });
      }
      useSwapRoutePersistStore.setState((state) => ({
        ...state,
        swapRoute: {
          ...state.swapRoute,
          fallbackAddress: osmosisAddress,
        },
      }));
    },
    [setConnectedWallet]
  );

  const handleKeplrAccountChanged = useCallback(async () => {
    if (cosmosChainId) {
      const address = await getCosmosKey(cosmosChainId, keplrTypeWallet);

      if (address) {
        await updateWalletStore(
          cosmosConnectedWallet,
          keplrTypeWallet,
          address
        );
      }
    }
  }, [
    cosmosChainId,
    keplrTypeWallet,
    updateWalletStore,
    cosmosConnectedWallet,
  ]);

  useEffect(() => {
    clientWindow?.addEventListener("keplr_keystorechange", () =>
      handleKeplrAccountChanged()
    );

    return () => {
      clientWindow?.removeEventListener("keplr_keystorechange", () =>
        handleKeplrAccountChanged()
      );
    };
  }, [handleKeplrAccountChanged, clientWindow]);

  type AddedChainInfo = Pick<
    ChainInfo,
    "chainId" | "chainName" | "bech32Config"
  >;

  const connectCosmos = useMutation(
    async ({
      chain,
      wallet,
      approveAllChains = true,
    }: {
      chain: ChainData;
      wallet: CosmosWallet;
      approveAllChains?: boolean;
    }): Promise<string | undefined> => {
      const chainInfos = getCosmosChainInfosObject(chain);
      const cosmosWalletObject = wallet.connector().provider;

      if (cosmosWalletObject) {
        setCosmosChainId(chainInfos.chainId.toString());
        try {
          // Check if this wallet supports batch enabling chains
          // If it does, we can use it to enable all the cosmos chains at once
          if (
            approveAllChains &&
            typeof cosmosWalletObject.getChainInfosWithoutEndpoints ===
              "function"
          ) {
            try {
              const addedChains: AddedChainInfo[] | undefined =
                await cosmosWalletObject.getChainInfosWithoutEndpoints();

              const addedChainIds = addedChains?.map(
                (addedChain) => addedChain.chainId
              );

              const chainsToEnable = cosmosChains
                .filter((c) => addedChainIds?.includes(c.chainId.toString()))
                .map((c) => c.chainId.toString());

              await cosmosWalletObject.enable(chainsToEnable);
            } catch (error) {
              // If the user rejected the batch enable request, throw an error to avoid
              // Fallback to a loop trying to enable each chain one by one
              if (isUserRejectionError(normalizeError(error))) {
                throw error;
              }

              console.warn(
                "Failed to get chain infos, falling back to single chain enable",
                error
              );
              await cosmosWalletObject.enable(chainInfos.chainId);
            }
          } else {
            await cosmosWalletObject.enable(chainInfos.chainId);
          }

          const address = await getAddress({
            chainId: chain.chainId.toString(),
            cosmosWalletObject,
            wallet,
          });

          if (address) {
            updateWalletStore(wallet, cosmosWalletObject, address);
            return address;
          }
        } catch (error) {
          await suggestChainOrThrow({
            chain,
            error,
            keplrTypeWallet: cosmosWalletObject,
          });

          return connectCosmos.mutateAsync({ chain, wallet });
        }
      }

      return undefined;
    }
  );

  const clearData = () => {
    disconnectWallet(ChainType.COSMOS);
  };

  const onCosmosChainChange = (newChainId: string) => {
    if (newChainId !== cosmosChainId) {
      clearData();
    }
  };

  return {
    connectCosmos,
    cosmosChainId,
    isConnected,
    onCosmosChainChange,
    cosmosConnectedWallet,
    cosmosSigner,
    keplrTypeWallet,
    getCosmosAddressForChain,
    cosmosAddress,
  };
};

export function useCosmosSigner({ chain }: { chain: ChainData | undefined }): {
  signer: OfflineSigner | undefined;
  keplrTypeWallet: Keplr | undefined;
} {
  const cosmosWalletState = useWalletStore(
    (state) => state.connectedWalletsByChainType[ChainType.COSMOS]
  ) as { wallet?: CosmosWallet; address?: string };

  /**
   * Get the appropriate Cosmos signer based on the chain
   * Noble chain requires full signer for CCTP messages
   * Other chains use Amino signer for Ledger compatibility
   */
  const getCosmosSignerForChain = useCallback(
    (chainId: string, wallet: Keplr): OfflineSigner => {
      return chainId === CHAIN_IDS.NOBLE
        ? wallet.getOfflineSigner(chainId)
        : wallet.getOfflineSignerOnlyAmino(chainId);
    },
    []
  );

  const connector = useMemo(() => {
    if (!cosmosWalletState?.wallet) {
      return undefined;
    }

    const cosmosConnector = getConnectorForChainType(
      cosmosWalletState.wallet,
      ChainType.COSMOS
    );

    if (!cosmosConnector) return undefined;

    return cosmosConnector()?.provider as Keplr;
  }, [cosmosWalletState?.wallet]);

  const signer = useMemo(() => {
    if (!connector || chain?.chainType !== ChainType.COSMOS) return;

    // use Amino signer for Ledger compatibility
    const newCosmosSigner = getCosmosSignerForChain(chain.chainId, connector);

    return newCosmosSigner;
  }, [chain?.chainId, chain?.chainType, connector]);

  return { signer, keplrTypeWallet: connector };
}
